import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import ProjectsService from "../../api/projectsService";
import TechnologiesService from "../../api/technologiesService";
import UploadService from "../../api/uploadService";
import MultipleImageUploader from "../../components/common/MultipleImageUploader";
import SingleImageUploader from "../../components/common/SingleImageUploader";

const CreateProjectPage = () => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      description: "",
      startYear: "",
      endYear: "",
      isActive: true,
      technologiesIds: [],
    },
  });

  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [thumbnailData, setThumbnailData] = useState(null);
  const [projectImages, setProjectImages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTechnologies = async () => {
      try {
        setLoading(true);
        const data = await TechnologiesService.getAll();
        setTechnologies(data);
      } catch (error) {
        console.error("Error fetching technologies:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTechnologies();
  }, []);

  const handleThumbnailSelected = (fileData) => {
    setThumbnailData(fileData);
  };

  const handleThumbnailRemoved = () => {
    // Release object URL to prevent memory leaks
    if (thumbnailData?.previewUrl) {
      URL.revokeObjectURL(thumbnailData.previewUrl);
    }
    setThumbnailData(null);
  };

  const handleProjectImagesUpdate = (updatedImages) => {
    setProjectImages(updatedImages);
  };

  // Upload a single file and return its URL
  const uploadFile = async (fileData) => {
    if (!fileData || !fileData.file) return null;
    try {
      const result = await UploadService.uploadImage(fileData.file);
      return result.url;
    } catch (error) {
      console.error(`Error uploading ${fileData.fileName}:`, error);
      throw new Error(`Failed to upload ${fileData.fileName}`);
    }
  };

  const onSubmit = async (formData) => {
    try {
      setSubmitting(true);

      // Upload thumbnail if selected
      let thumbnailUrl = null;
      if (thumbnailData && thumbnailData.file) {
        thumbnailUrl = await uploadFile(thumbnailData);
        if (!thumbnailUrl) {
          throw new Error("Failed to upload thumbnail image");
        }
      }

      // Upload project images if any
      const imageUrls = [];
      for (const imageData of projectImages) {
        // If it's a new image that needs to be uploaded
        if (imageData.isNew && imageData.file) {
          const uploadedUrl = await uploadFile(imageData);
          if (uploadedUrl) {
            imageUrls.push(uploadedUrl);
          }
        }
        // If it's an existing URL
        else if (imageData.url) {
          imageUrls.push(imageData.url);
        }
      }

      // Prepare data for submission with actual URLs and proper number conversions
      const dataToSubmit = {
        ...formData,
        thumbnail: thumbnailUrl,
        images: imageUrls,
        startYear: formData.startYear ? parseInt(formData.startYear, 10) : null,
        endYear: formData.endYear ? parseInt(formData.endYear, 10) : null,
      };

      await ProjectsService.create(dataToSubmit);
      navigate("/admin/projects");
    } catch (error) {
      console.error("Error creating project:", error);
      alert("Failed to create project. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Create New Project
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item size={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Project name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Project Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Controller
                name="description"
                control={control}
                rules={{ required: "Description is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    rows={4}
                    label="Project Description"
                    error={!!errors.description}
                    helperText={errors.description?.message}
                  />
                )}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="startYear"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Start Year"
                    type="number"
                    inputProps={{ min: 1990, max: new Date().getFullYear() }}
                    error={!!errors.startYear}
                    helperText={errors.startYear?.message}
                  />
                )}
              />
            </Grid>

            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                name="endYear"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="End Year"
                    type="number"
                    inputProps={{
                      min: 1990,
                      max: new Date().getFullYear() + 10,
                    }}
                    helperText="Leave empty if project is ongoing"
                    error={!!errors.endYear}
                  />
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                        {...field}
                      />
                    }
                    label="Project is active/published"
                  />
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item size={12}>
              <Typography variant="subtitle1" gutterBottom>
                Thumbnail Image
              </Typography>
              <Typography variant="caption" color="text.secondary" paragraph>
                This will be the main image displayed for the project in lists
                and cards.
              </Typography>
              <SingleImageUploader
                currentImage={thumbnailData?.previewUrl}
                onFileSelected={handleThumbnailSelected}
                onRemove={handleThumbnailRemoved}
                buttonLabel="Select Thumbnail"
              />
            </Grid>

            <Grid item size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item size={12}>
              <Typography variant="subtitle1" gutterBottom>
                Project Images
              </Typography>
              <Typography variant="caption" color="text.secondary" paragraph>
                Add multiple images showcasing different aspects of your
                project.
              </Typography>
              <MultipleImageUploader
                images={projectImages}
                onImagesUpdate={handleProjectImagesUpdate}
                buttonLabel="Add Project Images"
              />
            </Grid>

            <Grid item size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item size={12}>
              <Typography variant="subtitle1" gutterBottom>
                Technologies Used
              </Typography>
              <Controller
                name="technologiesIds"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    multiple
                    renderValue={(selected) => {
                      return technologies
                        .filter((tech) => selected.includes(tech.id))
                        .map((tech) => tech.name)
                        .join(", ");
                    }}
                  >
                    {technologies.map((tech) => (
                      <MenuItem key={tech.id} value={tech.id}>
                        {tech.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Grid>

            <Grid item size={12} sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    {thumbnailData || projectImages.length > 0
                      ? "Uploading Images..."
                      : "Creating Project..."}
                  </>
                ) : (
                  "Create Project"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/admin/projects")}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateProjectPage;
